import {
  ClippingPlaneCollection,
  ClippingPlane,
  Cartesian3,
  Color,
  defined,
  JulianDate,
  ModelGraphics,
  Model,
  Cesium3DTileset,
  createGuid,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";

/**
 * Property 값 가져오기
 * @private
 * @param {Property} property
 * @param {JulianDate} time
 */
const getPropertyValue = (property, time = new JulianDate()) => {
  let result;
  if (defined(property)) {
    if (defined(property.getValue)) {
      result = property.getValue(time);
    } else {
      result = property;
    }
  }

  return result;
};

/**
 * 절단면 분석 연산 및 영역 표시 클래스.</br>
 * Clipping analysis operation and area display class.
 * @alias AnalysisClipping
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = await Ditap.Model.fromGltfAsync({
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 1. 절단면 분석 인스턴스 생성
 * const analysisClipping = new AnalysisClipping(viewer, model);
 *
 * // 2. 절단면 생성
 * const planeX = analysisClipping.add(AnalysisClipping.PLANE_X_NORMAL);
 * // const planeY = analysisClipping.add(AnalysisClipping.PLANE_Y_NORMAL);
 * // const planeZ = analysisClipping.add(AnalysisClipping.PLANE_Z_NORMAL);
 *
 * // 3. 절단면 제거
 * analysisClipping.remove(planeX);
 * // analysisClipping.remove(planeY);
 * // analysisClipping.remove(planeZ);
 *
 * // 4. 절단면 거리 조절
 * analysisClipping.setDistance(planeX, 10);
 * // analysisClipping.setDistance(planeY, 10);
 * // analysisClipping.setDistance(planeZ, 10);
 */
class AnalysisClipping {
  constructor(viewer, object) {
    // id
    this._id = `AnalysisClipping${createGuid()}`;

    this._viewer = undefined;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      this._viewer.ditapStorage.analysisClipping[this._id] = this;
    } else {
      throw Error("AnalysisClipping viewer type error");
    }

    this._selectObject = undefined;
    if (
      object instanceof Model ||
      object instanceof Cesium3DTileset ||
      object instanceof ModelGraphics
    ) {
      this._selectObject = object;
    } else {
      throw Error("AnalysisClipping viewer type error");
    }

    this._planes = [];
  }

  /**
   * 사용할 Ditap Viewer 인스턴스. </br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /**
   * 절단면 분석 객체 ID. </br>
   * AnalysisClipping instance id.
   * @type {string}
   * @readonly
   */
  get id() {
    return this._id;
  }

  /**
   * 절단면 분석 오브젝트. </br>
   * Clipping plane analysis object.
   * @type {Model | Cesium3DTileset | ModelGraphics}
   * @readonly
   */
  get selectObject() {
    return this._selectObject;
  }

  /**
   * 적용중인 절단면. <br>
   * Clipping plane being applied.
   * @type {ClippingPlane[]}
   * @readonly
   */
  get planes() {
    return this._planes;
  }

  /**
   * 절단면 추가.</br>
   * Add clipping plane
   * @param {String | Cartesian3} normal 축 ["X", "Y", "Z"] 또는 법선벡터.</br> Axis ["X", "Y", "Z"] or normal vector.
   * @param {number} distance 거리. </br> Normal distance.
   * @returns {ClippingPlane} 생성된 절단면 객체.</br> Clipping plane object.
   */
  add(normal, distance = 0) {
    // 절단면 생성
    const plane = new ClippingPlane(normal, distance);

    // 절단면 추가
    try {
      const selectObject = this._selectObject;
      if (selectObject.clippingPlanes instanceof ClippingPlaneCollection) {
        selectObject.clippingPlanes.add(plane);
      } else if (defined(selectObject?.clippingPlanes?.getValue)) {
        const property = getPropertyValue(selectObject.clippingPlanes);
        property.add(plane);
        selectObject.clippingPlanes = property;
      } else {
        const planeCollection = new ClippingPlaneCollection({
          planes: [plane],
          edgeColor: Color.RED.withAlpha(0.1),
          edgeWidth: 1.0,
        });
        selectObject.clippingPlanes = planeCollection;
      }

      this._planes.push(plane);

      return plane;
    } catch (error) {
      throw Error(`AnalysisClipping.add ${error}`);
    }
  }

  /**
   * 절단면 위치 조절.</br>
   * Move in normal direction
   * @param {ClippingPlane} plane 이동시킬 절단면.</br> The model to be moved.
   * @param {number} distance 이동 거리 (미터).</br> Movement amount (meters).
   * @returns {plane} 위치 조절된 절단면. <br> Position adjusted cutting plane.
   */
  setDistance(plane, distance) {
    if (!(plane instanceof ClippingPlane)) {
      throw Error("AnalysisClipping.setDistance plane parameter is invalid");
    }

    try {
      plane.distance = distance;

      return plane;
    } catch (error) {
      throw Error(`AnalysisClipping.setDistance ${error}`);
    }
  }

  /**
   * 절단면 노멀 재설정. <br>
   * Reset clipping plane normal.
   * @param {ClippingPlane} plane 재설정 절단면. <br> Reset clipping plane.
   * @param {Cartesian3} normal 재설정 노멀. <br> Reset Normal.
   * @returns {plane} plane 노멀 재설정된 절단면. <br> Normal reset clipping plane.
   */
  setNormal(plane, normal) {
    if (!(plane instanceof ClippingPlane)) {
      throw Error("AnalysisClipping.setNormal plane parameter is invalid");
    }

    try {
      plane.normal = normal;

      return plane;
    } catch (error) {
      throw Error(`AnalysisClipping.setNormal ${error}`);
    }
  }

  /**
   * 절단면 제거.</br>
   * Remove the entered clipping plane
   * @param {ClippingPlane} plane 제거할 절단면.</br> The clipping plane to be removed
   * @returns {boolean} 절단면 제거 여부.</br> Whether to remove clipping plane.
   */
  remove(plane) {
    if (!(plane instanceof ClippingPlane)) {
      throw Error("AnalysisClipping.remove plane parameter is invalid");
    }

    try {
      const selectObject = this._selectObject;

      let result;
      if (selectObject.clippingPlanes instanceof ClippingPlaneCollection) {
        result = selectObject.clippingPlanes.remove(plane);
      } else if (defined(selectObject?.clippingPlanes?.getValue)) {
        const property = getPropertyValue(selectObject.clippingPlanes);
        result = property.remove(plane);
        selectObject.clippingPlanes = property;
      }

      const index = this._planes.indexOf(plane);
      if (-1 < index) {
        this._planes.splice(index, 1);
      }

      return result;
    } catch (error) {
      throw Error(`AnalysisClipping.remove ${error}`);
    }
  }

  /**
   * 입력된 노멀에 해당하는 절단면 추출. <br>
   * Extract clipping plane corresponding to input normal.
   *
   * @param {Cartesian3} normal
   * @returns {ClippingPlane | undefined} clippingPlane
   */
  getPlaneByNormal(normal) {
    if (!(normal instanceof Cartesian3)) {
      throw Error(
        "AnalysisClipping.getPlaneByNormal normal parameter is invalid"
      );
    }

    const planes = this._planes;
    for (const plane of planes) {
      const planeNormal = plane.normal;
      if (Cartesian3.equals(normal, planeNormal)) {
        return plane;
      }
    }

    return undefined;
  }

  /**
   * 모든 절단면 제거. <br>
   * Remove all created clipping planes
   */
  clear() {
    const selectObject = this._selectObject;
    const planes = this._planes;
    for (const plane of planes) {
      if (selectObject?.clippingPlanes instanceof ClippingPlaneCollection) {
        selectObject.clippingPlanes.remove(plane);
      } else if (defined(selectObject?.clippingPlanes?.getValue)) {
        const property = getPropertyValue(selectObject.clippingPlanes);
        property.remove(plane);
        selectObject.clippingPlanes = property;
      }
    }

    this._planes = [];
  }

  /**
   * 인스턴스 제거. <br>
   * Destroys the instance.
   */
  destroy() {
    this.clear();
    this._viewer.ditapStorage.removeInstanceById(this._id);
  }
}

// 클래스 내부에서 상수 프로퍼티 설정 시 커밋 오류 발생함.
/**
 * 월드좌표 X 방향 노멀. <br>
 * World coordinates X direction normal.
 * @constant
 * @type {Cartesian3}
 */
AnalysisClipping.PLANE_X_NORMAL = new Cartesian3(-1.0, 0.0, 0.0);

/**
 * 월드좌표 Y 방향 노멀. <br>
 * World coordinates Y direction normal.
 * @constant
 * @type {Cartesian3}
 */
AnalysisClipping.PLANE_Y_NORMAL = new Cartesian3(0.0, 1.0, 0.0);

/**
 * 월드좌표 Z 방향 노멀. <br>
 * World coordinates Z direction normal.
 * @constant
 * @type {Cartesian3}
 */
AnalysisClipping.PLANE_Z_NORMAL = new Cartesian3(0.0, 0.0, -1.0);

export default AnalysisClipping;
